/**
 * FailureAnalysisPanel - Detaillierte Analyse von kritischen 3-Topf-Failure-Szenarien
 *
 * Diese spezialisierte React-Komponente bietet eine tiefgehende Analyse der Szenarien,
 * in denen die 3-Topf-Strategie früher versagt als die 1-Topf-Strategie.
 *
 * Hauptfunktionen:
 * - Detaillierte Visualisierung von Failure-Pattern
 * - Jahresweise Depletion-Analyse  
 * - Marktkontext für kritische Szenarien
 * - Export von Worst-Case-Daten
 * - Risiko-Mitigation-Empfehlungen
 *
 * @author Portfolio-3Topf Team
 * @version 1.0.0
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  BarChart,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Popover,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import {
  CombinedMonteCarloEngine,
  CombinedMonteCarloResult,
  CombinedMonteCarloStats,
} from '@/services/CombinedMonteCarloEngine';
import { SteuerParameter } from '@/types';
import { IterationDetailModal } from './IterationDetailModal';

interface FailureAnalysisPanelProps {
  monteCarloResults: CombinedMonteCarloResult[];
  monteCarloStats: CombinedMonteCarloStats;
  steuerParameter: SteuerParameter;
  monteCarloEngine?: CombinedMonteCarloEngine;
  crisisDefinition: 'negative-return' | 'kapitalindex-drawdown';
}

export const FailureAnalysisPanel: React.FC<FailureAnalysisPanelProps> = ({
  monteCarloResults,
  monteCarloStats,
  steuerParameter,
  monteCarloEngine,
  crisisDefinition,
}) => {
  // State für erweiterte Anzeige
  const [showAllCriticalAges, setShowAllCriticalAges] = useState(false);
  
  // State für Detail-Modal
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIteration, setSelectedIteration] = useState<number>(0);
  const [selectedResult, setSelectedResult] = useState<CombinedMonteCarloResult | undefined>(undefined);
  const [selectedScenarioType, setSelectedScenarioType] = useState<'worst-case-3topf' | 'worst-case-1topf' | 'general'>('general');
  const [selectedScenarioDescription, setSelectedScenarioDescription] = useState<string>('');

  // Handler für Drill-Down Modal
  // CRITICAL FIX v2.4.12: Pass the actual result object directly to ensure 100% data consistency
  // This eliminates any possibility of engine instance mismatch or lost stored results
  const handleShowIterationDetails = (
    iteration: number, 
    scenarioType: 'worst-case-3topf' | 'worst-case-1topf' | 'general' = 'general'
  ) => {
    if (!monteCarloEngine) {
      console.warn('Monte Carlo Engine nicht verfügbar für Drill-Down');
      return;
    }
    
    // Find the result in monteCarloResults (component state - SINGLE SOURCE OF TRUTH)
    const result = monteCarloResults.find(r => r.iteration === iteration);
    
    if (!result) {
      console.error(`Iteration ${iteration} not found in monteCarloResults`);
      return;
    }
    
    setSelectedIteration(iteration);
    setSelectedResult(result); // Pass the actual result object
    setSelectedScenarioType(scenarioType);
    setSelectedScenarioDescription(''); // Will be generated by modal from actual data
    setModalVisible(true);
  };
  
  // Analyse der Failure-Pattern nach Alter
  const failureByAgeAnalysis = useMemo(() => {
    if (!monteCarloResults || monteCarloResults.length === 0 || !steuerParameter) {return null;}

    const ageGroups: {
      [age: number]: { 
        threeTopf: number; 
        oneTopf: number;
        threeTopfIterations: number[];
        oneTopfIterations: number[];
      } 
    } = {};
    
    // Failure-nach-Alter (verfeinerte Erfolgs-Definition, 95%-Zieljahr-Schwelle):
    // - Failure == r.*.successfulStrategy === false (Engine-Flag: Zielalter nicht erreicht
    //   ODER Zieljahr unterfinanziert).
    // - Failure-Alter = alter des letzten Eintrags: früh erschöpft → vor dem Zielalter;
    //   "Zieljahr unterfinanziert" → genau das Zielalter. Solche Läufe verschwinden damit
    //   NICHT mehr stillschweigend, sondern erscheinen als Failure-Bucket beim Zielalter.
    monteCarloResults.forEach((result) => {
      // CRITICAL: Skip iterations without jahresergebnisse (old data)
      // We cannot reliably determine depletion age without the actual year data
      if (!result.threeTopf.jahresergebnisse || result.threeTopf.jahresergebnisse.length === 0 ||
          !result.oneTopf.jahresergebnisse || result.oneTopf.jahresergebnisse.length === 0) {
        return; // Skip this iteration - data is incomplete
      }

      // 3-Topf: Failure per Engine-Flag, gruppiert nach Failure-Alter (letzter Eintrag)
      if (!result.threeTopf.successfulStrategy) {
        const age = result.threeTopf.jahresergebnisse[result.threeTopf.jahresergebnisse.length - 1].alter;
        if (!ageGroups[age]) {ageGroups[age] = { threeTopf: 0, oneTopf: 0, threeTopfIterations: [], oneTopfIterations: [] };}
        ageGroups[age].threeTopf++;
        ageGroups[age].threeTopfIterations.push(result.iteration);
      }

      // 1-Topf: analog
      if (!result.oneTopf.successfulStrategy) {
        const age = result.oneTopf.jahresergebnisse[result.oneTopf.jahresergebnisse.length - 1].alter;
        if (!ageGroups[age]) {ageGroups[age] = { threeTopf: 0, oneTopf: 0, threeTopfIterations: [], oneTopfIterations: [] };}
        ageGroups[age].oneTopf++;
        ageGroups[age].oneTopfIterations.push(result.iteration);
      }
    });
    
    const result = Object.entries(ageGroups)
      .map(([age, counts]) => ({
        age: parseInt(age),
        threeTopfFailures: counts.threeTopf,
        oneTopfFailures: counts.oneTopf,
        threeTopfIterations: counts.threeTopfIterations,
        oneTopfIterations: counts.oneTopfIterations,
        difference: counts.threeTopf - counts.oneTopf,
      }))
      .filter(item => item.threeTopfFailures > 0 || item.oneTopfFailures > 0) // Nur Ages mit tatsächlichen Erschöpfungen
      .sort((a, b) => a.age - b.age);

    return result;
  }, [monteCarloResults, steuerParameter]);

  // Calculate early failure iterations for KPI display
  const earlyFailureIterations = useMemo(() => {
    if (!monteCarloResults || monteCarloResults.length === 0) {
      return { threeTopfEarly: [], oneTopfEarly: [] };
    }
    
    const threeTopfEarly: number[] = [];
    const oneTopfEarly: number[] = [];
    
    monteCarloResults.forEach(r => {
      if (r.failureAnalysis.earlyFailureType === '3-topf-early') {
        threeTopfEarly.push(r.iteration);
      } else if (r.failureAnalysis.earlyFailureType === '1-topf-early') {
        oneTopfEarly.push(r.iteration);
      }
    });
    
    return { threeTopfEarly, oneTopfEarly };
  }, [monteCarloResults]);

  // Chart-Daten für Failure-Distribution
  const failureDistributionChartData = useMemo(() => {
    if (!failureByAgeAnalysis) {return [];}

    return [
      {
        title: '3-Topf Failures',
        type: 'bar' as const,
        data: failureByAgeAnalysis.map(item => ({
          x: item.age,
          y: item.threeTopfFailures,
        })),
        color: '#d13212', // Error red
      },
      {
        title: '1-Topf Failures',
        type: 'bar' as const, 
        data: failureByAgeAnalysis.map(item => ({
          x: item.age,
          y: item.oneTopfFailures,
        })),
        color: '#ff9900', // Warning orange
      },
    ];
  }, [failureByAgeAnalysis]);

  // Calculate total failures for each strategy
  const totalFailures = useMemo(() => {
    if (!failureByAgeAnalysis) {return { threeTopf: 0, oneTopf: 0 };}
    
    return {
      threeTopf: failureByAgeAnalysis.reduce((sum, item) => sum + item.threeTopfFailures, 0),
      oneTopf: failureByAgeAnalysis.reduce((sum, item) => sum + item.oneTopfFailures, 0),
    };
  }, [failureByAgeAnalysis]);

  // Alle Altersgruppen mit Depletions (sorted by age)
  const depletionsByAge = useMemo(() => {
    if (!failureByAgeAnalysis) {return { displayed: [], total: 0, all: [] };}

    const allAges = failureByAgeAnalysis
      .sort((a, b) => a.age - b.age); // Sortiert: nach Alter aufsteigend

    const displayedAges = showAllCriticalAges ? allAges : allAges.slice(0, 5);

    return {
      displayed: displayedAges,
      total: allAges.length,
      all: allAges,
    };
  }, [failureByAgeAnalysis, showAllCriticalAges]);

  // Crisis Resilience Chart Data - Grouped by crisis years
  const crisisResilienceData = useMemo(() => {
    if (!monteCarloResults || monteCarloResults.length === 0 || !steuerParameter || !monteCarloEngine) {
      return {
        chartData: [],
        maxCrisisYears: 0,
        avgCrisisYears: 0,
        totalScenarios: 0,
      };
    }

    // Group results by crisis year ranges
    const crisisGroups: {
      [range: string]: {
        threeTopfSuccess: number;
        threeTopfFailure: number;
        oneTopfSuccess: number;
        oneTopfFailure: number;
      };
    } = {};

    let totalCrisisYears = 0;
    let maxCrisis = 0;

    monteCarloResults.forEach((result) => {
      // Count crisis years using the ACTUAL crisis definition (kapitalindex-drawdown)
      // Crisis = when kapitalIndex is below the historical peak
      let crisisYears = 0;
      
      const kapitalIndex = result.threeTopf.kapitalIndex;
      // Anchor at 100 (pre-retirement baseline), NOT kapitalIndex[0]: the first
      // array entry already contains the year-0 return (= 100 * (1 + r0)), so a
      // crash in year 0 (e.g. index 85) would otherwise never be counted as a
      // crisis year and the 85→100 recovery stretch would be missed entirely.
      // Same anchor-off-by-one class of bug already fixed for max drawdown in
      // PerformanceCalculationService.calculateEmpiricalMaxDrawdownPercent.
      // Note: stored indices are rounded (see CombinedMonteCarloEngine), so
      // sub-0.5-point drawdowns are invisible here by data design, not a bug.
      let peak = 100;

      for (let i = 0; i < kapitalIndex.length; i++) {
        const currentIndex = kapitalIndex[i];

        // Crisis: current index is below the historical peak
        if (currentIndex < peak) {
          crisisYears++;
        } else {
          // Update peak if current index is higher
          peak = Math.max(peak, currentIndex);
        }
      }
      
      totalCrisisYears += crisisYears;
      maxCrisis = Math.max(maxCrisis, crisisYears);

      // Categorize into ranges
      let range: string;
      if (crisisYears <= 5) {range = '0-5 Jahre';}
      else if (crisisYears <= 10) {range = '6-10 Jahre';}
      else if (crisisYears <= 15) {range = '11-15 Jahre';}
      else {range = '16+ Jahre';}

      if (!crisisGroups[range]) {
        crisisGroups[range] = {
          threeTopfSuccess: 0,
          threeTopfFailure: 0,
          oneTopfSuccess: 0,
          oneTopfFailure: 0,
        };
      }

      // Count successes and failures
      if (result.threeTopf.successfulStrategy) {
        crisisGroups[range].threeTopfSuccess++;
      } else {
        crisisGroups[range].threeTopfFailure++;
      }

      if (result.oneTopf.successfulStrategy) {
        crisisGroups[range].oneTopfSuccess++;
      } else {
        crisisGroups[range].oneTopfFailure++;
      }
    });

    // Convert to chart format
    const chartData = Object.entries(crisisGroups)
      .map(([range, counts]) => ({
        x: range,
        y: counts.threeTopfSuccess + counts.threeTopfFailure + counts.oneTopfSuccess + counts.oneTopfFailure,
        threeTopfSuccess: counts.threeTopfSuccess,
        threeTopfFailure: counts.threeTopfFailure,
        oneTopfSuccess: counts.oneTopfSuccess,
        oneTopfFailure: counts.oneTopfFailure,
      }))
      .sort((a, b) => {
        const order = ['0-5 Jahre', '6-10 Jahre', '11-15 Jahre', '16+ Jahre'];
        return order.indexOf(a.x) - order.indexOf(b.x);
      });

    return {
      chartData,
      maxCrisisYears: maxCrisis,
      avgCrisisYears: totalCrisisYears / monteCarloResults.length,
      totalScenarios: monteCarloResults.length,
    };
  }, [monteCarloResults, steuerParameter, monteCarloEngine]);


  if (!monteCarloStats?.failureAnalysis) {
    return (
      <Box textAlign="center" color="text-body-secondary">
        <p>Keine Failure-Analyse-Daten verfügbar</p>
      </Box>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Early Failure Iterations */}
      {(earlyFailureIterations.threeTopfEarly.length > 0 || earlyFailureIterations.oneTopfEarly.length > 0) && (
        <Container>
          <Header variant="h3" description="Iterationen, in denen eine Strategie früher als die andere versagte">
            Early Failure Iterationen
          </Header>
          
          <ColumnLayout columns={2} variant="text-grid">
            {earlyFailureIterations.threeTopfEarly.length > 0 && (
              <div>
                <Box variant="h4" color="text-status-error">
                  3-Topf Early Failures ({earlyFailureIterations.threeTopfEarly.length})
                </Box>
                <Box variant="small" color="text-body-secondary">
                  3-Topf versagte vor 1-Topf
                </Box>
                <div style={{ marginTop: '16px' }}>
                  <Popover
                    dismissButton={false}
                    position="right"
                    size="large"
                    triggerType="custom"
                    content={
                      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px' }}>
                        <SpaceBetween size="xs">
                          <Box variant="strong">{earlyFailureIterations.threeTopfEarly.length} Iterationen</Box>
                          {earlyFailureIterations.threeTopfEarly.slice(0, 20).map((iter: number) => (
                            <Button
                              key={iter}
                              variant="inline-link"
                              onClick={() => handleShowIterationDetails(iter, 'worst-case-3topf')}
                            >
                              Iteration #{iter}
                            </Button>
                          ))}
                          {earlyFailureIterations.threeTopfEarly.length > 20 && (
                            <Box variant="small" color="text-body-secondary">
                              ... und {earlyFailureIterations.threeTopfEarly.length - 20} weitere
                            </Box>
                          )}
                        </SpaceBetween>
                      </div>
                    }
                  >
                    <Button variant="normal" disabled={!monteCarloEngine}>
                      Iterationen ansehen ({Math.min(20, earlyFailureIterations.threeTopfEarly.length)})
                    </Button>
                  </Popover>
                </div>
              </div>
            )}
            
            {earlyFailureIterations.oneTopfEarly.length > 0 && (
              <div>
                <Box variant="h4" color="text-status-warning">
                  1-Topf Early Failures ({earlyFailureIterations.oneTopfEarly.length})
                </Box>
                <Box variant="small" color="text-body-secondary">
                  1-Topf versagte vor 3-Topf
                </Box>
                <div style={{ marginTop: '16px' }}>
                  <Popover
                    dismissButton={false}
                    position="right"
                    size="large"
                    triggerType="custom"
                    content={
                      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px' }}>
                        <SpaceBetween size="xs">
                          <Box variant="strong">{earlyFailureIterations.oneTopfEarly.length} Iterationen</Box>
                          {earlyFailureIterations.oneTopfEarly.slice(0, 20).map((iter: number) => (
                            <Button
                              key={iter}
                              variant="inline-link"
                              onClick={() => handleShowIterationDetails(iter, 'worst-case-1topf')}
                            >
                              Iteration #{iter}
                            </Button>
                          ))}
                          {earlyFailureIterations.oneTopfEarly.length > 20 && (
                            <Box variant="small" color="text-body-secondary">
                              ... und {earlyFailureIterations.oneTopfEarly.length - 20} weitere
                            </Box>
                          )}
                        </SpaceBetween>
                      </div>
                    }
                  >
                    <Button variant="normal" disabled={!monteCarloEngine}>
                      Iterationen ansehen ({Math.min(20, earlyFailureIterations.oneTopfEarly.length)})
                    </Button>
                  </Popover>
                </div>
              </div>
            )}
          </ColumnLayout>
        </Container>
      )}
      
      {/* Premature Failures by Age */}
      <Container>
        <Header 
          variant="h2" 
          description="Übersicht der vorzeitigen Portfolio-Erschöpfungen nach Lebensalter"
        >
          Vorzeitige Failures nach Alter
        </Header>
        
        <SpaceBetween direction="vertical" size="m">
          {/* Depletions by Age Table */}
          <Table
            columnDefinitions={[
                  {
                    id: 'age',
                    header: 'Alter',
                    cell: (item: any) => `${item.age} Jahre`,
                  },
                  {
                    id: 'threeTopfFailures',
                    header: `3-Topf Failures (${totalFailures.threeTopf})`,
                    cell: (item: any) => (
                      <Badge color="red">{item.threeTopfFailures}</Badge>
                    ),
                  },
                  {
                    id: 'oneTopfFailures',
                    header: `1-Topf Failures (${totalFailures.oneTopf})`,
                    cell: (item: any) => (
                      <Badge color="blue">{item.oneTopfFailures}</Badge>
                    ),
                  },
                  {
                    id: 'difference',
                    header: 'Differenz',
                    cell: (item: any) => (
                      <Badge color={item.difference > 0 ? (item.difference > 3 ? 'red' : 'grey') : 'blue'}>
                        {item.difference > 0 
                          ? `+${item.difference} mehr 3-Topf Failures`
                          : `${item.difference} (1-Topf häufiger)`
                        }
                      </Badge>
                    ),
                  },
                  {
                    id: 'actions',
                    header: 'Szenarien ansehen',
                    cell: (item: any) => {
                      // Show dropdown for 3-Topf if there are failures, else 1-Topf
                      const hasThreeTopf = item.threeTopfIterations && item.threeTopfIterations.length > 0;
                      const hasOneTopf = item.oneTopfIterations && item.oneTopfIterations.length > 0;
                      
                      if (!hasThreeTopf && !hasOneTopf) {return null;}
                      
                      return (
                        <SpaceBetween direction="horizontal" size="xs">
                          {hasThreeTopf && (
                            <Popover
                              dismissButton={false}
                              position="top"
                              size="large"
                              triggerType="custom"
                              content={
                                <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px' }}>
                                  <SpaceBetween size="xs">
                                    <Box variant="strong">{item.threeTopfFailures} 3-Topf Szenarien</Box>
                                    {item.threeTopfIterations.slice(0, 20).map((iter: number) => (
                                      <Button
                                        key={iter}
                                        variant="inline-link"
                                        onClick={() => handleShowIterationDetails(iter, 'general')}
                                      >
                                        Iteration #{iter}
                                      </Button>
                                    ))}
                                    {item.threeTopfIterations.length > 20 && (
                                      <Box variant="small" color="text-body-secondary">
                                        ... und {item.threeTopfIterations.length - 20} weitere
                                      </Box>
                                    )}
                                  </SpaceBetween>
                                </div>
                              }
                            >
                              <Button variant="normal" disabled={!monteCarloEngine}>
                                3-Topf ({item.threeTopfFailures})
                              </Button>
                            </Popover>
                          )}
                          {hasOneTopf && (
                            <Popover
                              dismissButton={false}
                              position="top"
                              size="large"
                              triggerType="custom"
                              content={
                                <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px' }}>
                                  <SpaceBetween size="xs">
                                    <Box variant="strong">{item.oneTopfFailures} 1-Topf Szenarien</Box>
                                    {item.oneTopfIterations.slice(0, 20).map((iter: number) => (
                                      <Button
                                        key={iter}
                                        variant="inline-link"
                                        onClick={() => handleShowIterationDetails(iter, 'general')}
                                      >
                                        Iteration #{iter}
                                      </Button>
                                    ))}
                                    {item.oneTopfIterations.length > 20 && (
                                      <Box variant="small" color="text-body-secondary">
                                        ... und {item.oneTopfIterations.length - 20} weitere
                                      </Box>
                                    )}
                                  </SpaceBetween>
                                </div>
                              }
                            >
                              <Button variant="normal" disabled={!monteCarloEngine}>
                                1-Topf ({item.oneTopfFailures})
                              </Button>
                            </Popover>
                          )}
                        </SpaceBetween>
                      );
                    },
                  },
            ]}
            items={depletionsByAge.displayed}
            sortingDisabled
            empty={
              <Box textAlign="center" color="inherit">
                Keine vorzeitigen Depletions vorhanden
              </Box>
            }
          />
          {depletionsByAge.total > 5 && (
            <Button
              variant="link"
              onClick={() => setShowAllCriticalAges(!showAllCriticalAges)}
            >
              {showAllCriticalAges 
                ? 'Nur erste 5 anzeigen' 
                : `Alle ${depletionsByAge.total} Altersgruppen anzeigen`
              }
            </Button>
          )}
        </SpaceBetween>
      </Container>

      {/* Failure Distribution Chart */}
      {failureDistributionChartData.length > 0 && (
        <Container>
          <Header 
            variant="h3" 
            description="Verteilung der vorzeitigen Portfolio-Erschöpfung nach Lebensalter (nur Failures vor Zielalter). Klicken Sie auf 'Kritische Altersgruppen' oben für detaillierte Szenario-Auswahl."
          >
            Failure-Verteilung nach Alter (3-Topf: {totalFailures.threeTopf} Failures, 1-Topf: {totalFailures.oneTopf} Failures)
          </Header>
          
          <BarChart
            series={failureDistributionChartData}
            xTitle="Lebensalter"
            yTitle="Anzahl Failures"
            height={400}
            statusType="finished"
            hideFilter={true}
            empty={
              <Box textAlign="center" color="inherit">
                <Box variant="strong" textAlign="center" color="inherit">
                  Keine Failure-Daten verfügbar
                </Box>
              </Box>
            }
            xScaleType="categorical"
          />
        </Container>
      )}

      {/* Crisis Resilience Chart */}
      {crisisResilienceData.chartData.length > 0 && (
        <Container>
          <Header 
            variant="h3" 
            description="Verteilung der Erfolge und Failures nach Anzahl der Krisenjahre. Zeigt die Resilienz beider Strategien gegenüber unterschiedlich starker Krisenbelastung."
          >
            Krisen-Resilienz-Analyse
          </Header>
          
          <SpaceBetween direction="vertical" size="m">
            <ColumnLayout columns={4} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Durchschnittliche Krisenjahre</Box>
                <Box variant="awsui-value-large">{crisisResilienceData.avgCrisisYears.toFixed(1)}</Box>
                <Box variant="small" color="text-body-secondary">Jahre pro Simulation</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Maximum Krisenjahre</Box>
                <Box variant="awsui-value-large">{crisisResilienceData.maxCrisisYears}</Box>
                <Box variant="small" color="text-body-secondary">Schlimmstes Szenario</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Gesamt-Szenarien</Box>
                <Box variant="awsui-value-large">{crisisResilienceData.totalScenarios}</Box>
                <Box variant="small" color="text-body-secondary">Analysierte Simulationen</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Empfohlene Planung</Box>
                <Box variant="awsui-value-large">
                  {Math.ceil(crisisResilienceData.avgCrisisYears * 1.5)}
                </Box>
                <Box variant="small" color="text-body-secondary">Krisenjahre einplanen</Box>
              </div>
            </ColumnLayout>

            <Alert type="info" header="Interpretation">
              <SpaceBetween direction="vertical" size="xs">
                <div>
                  <strong>Leseweise:</strong> Das Diagramm zeigt, wie viele Portfolios bei verschiedenen 
                  Krisenbelastungen erfolgreich waren oder versagt haben.
                </div>
                <div>
                  <strong>Grün:</strong> 3-Topf Erfolgreiche (erreichten Zielalter)
                </div>
                <div>
                  <strong>Rot:</strong> 3-Topf Failures (vorzeitig erschöpft)
                </div>
                <div>
                  <strong>Blau:</strong> 1-Topf Erfolgreiche (erreichten Zielalter)
                </div>
                <div>
                  <strong>Orange:</strong> 1-Topf Failures (vorzeitig erschöpft)
                </div>
              </SpaceBetween>
            </Alert>

            <BarChart
              series={[
                {
                  title: '3-Topf Erfolgreich',
                  type: 'bar',
                  data: crisisResilienceData.chartData.map(d => ({ x: d.x, y: d.threeTopfSuccess })),
                  color: '#037f0c',
                },
                {
                  title: '3-Topf Versagt',
                  type: 'bar',
                  data: crisisResilienceData.chartData.map(d => ({ x: d.x, y: d.threeTopfFailure })),
                  color: '#d13212',
                },
                {
                  title: '1-Topf Erfolgreich',
                  type: 'bar',
                  data: crisisResilienceData.chartData.map(d => ({ x: d.x, y: d.oneTopfSuccess })),
                  color: '#0073bb',
                },
                {
                  title: '1-Topf Versagt',
                  type: 'bar',
                  data: crisisResilienceData.chartData.map(d => ({ x: d.x, y: d.oneTopfFailure })),
                  color: '#ff9900',
                },
              ]}
              xTitle="Krisenbelastung (Anzahl Krisenjahre)"
              yTitle="Anzahl Portfolios"
              height={400}
              statusType="finished"
              hideFilter={true}
              stackedBars
              xScaleType="categorical"
              empty={
                <Box textAlign="center" color="inherit">
                  <Box variant="strong">Keine Daten verfügbar</Box>
                  <Box variant="p">Führen Sie eine Monte-Carlo-Simulation durch.</Box>
                </Box>
              }
            />

            <Alert type="success" header="Erkenntnisse aus der Krisen-Resilienz-Analyse">
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>
                  <strong>Resilienz-Schwelle:</strong> Die meisten Portfolios überleben bis zu{' '}
                  {Math.floor(crisisResilienceData.avgCrisisYears)} Krisenjahre erfolgreich
                </li>
                <li>
                  <strong>3-Topf Vorteil:</strong> Grüne Balken (erfolgreiche 3-Topf) bleiben auch bei
                  höherer Krisenbelastung stark vertreten
                </li>
                <li>
                  <strong>Kritische Zone:</strong> Rote und orange Balken (Failures) nehmen zu,
                  wenn die Krisenbelastung steigt
                </li>
                <li>
                  <strong>Strategie-Vergleich:</strong> Der Anteil grüner vs. blauer Balken zeigt,
                  welche Strategie bei unterschiedlicher Krisenbelastung überlegen ist
                </li>
                <li>
                  <strong>Optimale Vorbereitung:</strong> Planen Sie für mindestens{' '}
                  {Math.ceil(crisisResilienceData.avgCrisisYears * 1.5)} Krisenjahre ein
                </li>
              </ul>
            </Alert>
          </SpaceBetween>
        </Container>
      )}


      {/* Detail-Modal für Drill-Down */}
      {monteCarloEngine && (
        <IterationDetailModal
          isVisible={modalVisible}
          onDismiss={() => setModalVisible(false)}
          iteration={selectedIteration}
          result={selectedResult}
          monteCarloEngine={monteCarloEngine}
          scenarioType={selectedScenarioType}
          scenarioDescription={selectedScenarioDescription}
          crisisDefinition={crisisDefinition}
        />
      )}
    </SpaceBetween>
  );
};
